import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ScrollToTop from "@/components/base/ScrollToTop";
import Seo from "@/components/base/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { categories } from "@/mocks/categories";
import { getAllArticles, formatViews, getCategoryName } from "@/lib/content";

const ORIGIN = "https://sjlm888.cn";

export default function RankPage() {
  const [active, setActive] = useState<string>("all");

  const ranked = useMemo(() => {
    const all = getAllArticles();
    const filtered =
      active === "all" ? all : all.filter((item) => item.category === active);
    return [...filtered].sort((a, b) => b.heat - a.heat);
  }, [active]);

  const maxHeat = ranked[0]?.heat ?? 1;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "今日热瓜榜 - 51吃瓜",
      description: "51吃瓜今日热瓜榜，按阅读与讨论热度排序，实时同步最新动态。",
      url: `${ORIGIN}/rank`,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "今日热瓜榜",
      itemListElement: ranked.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `${ORIGIN}/article/${item.id}`,
        name: item.title,
      })),
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Seo
        title="今日热瓜榜 - 51吃瓜每日新瓜排行"
        description="51吃瓜今日热瓜榜按阅读与讨论热度实时排序，涵盖明星动态、影视追踪、综艺看点、音乐现场与时尚风向，今天有什么瓜一看便知。"
        keywords="51吃瓜,51吃瓜今日新鲜瓜,今日热瓜榜,51吃瓜每日新瓜,今天有什么瓜"
        path="/rank"
        jsonLd={jsonLd}
      />
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full px-4 md:px-8 pt-24 md:pt-32 pb-8 bg-background-100">
          <div className="max-w-content mx-auto">
            <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "今日热瓜榜" }]} />
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-[0.2em] text-primary-600 uppercase">
                <span className="w-4 h-px bg-primary-500"></span>
                Ranking
              </span>
              <h1 className="mt-3 font-heading font-black text-3xl md:text-4xl text-foreground-950">
                今日热瓜榜
              </h1>
              <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                综合阅读量与讨论热度实时排序，随时刷新。想快速知道今天有什么瓜，看这一页就够了。
              </p>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActive("all")}
                className={`px-3.5 py-1.5 rounded-full text-xs md:text-sm border transition-colors whitespace-nowrap cursor-pointer ${
                  active === "all"
                    ? "bg-primary-500 text-background-50 border-primary-500"
                    : "bg-background-50 text-foreground-700 border-background-200 hover:border-primary-300"
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  type="button"
                  onClick={() => setActive(cat.slug)}
                  className={`px-3.5 py-1.5 rounded-full text-xs md:text-sm border transition-colors whitespace-nowrap cursor-pointer ${
                    active === cat.slug
                      ? "bg-primary-500 text-background-50 border-primary-500"
                      : "bg-background-50 text-foreground-700 border-background-200 hover:border-primary-300"
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="max-w-content mx-auto">
            <ol className="space-y-4">
              {ranked.map((item, index) => (
                <li key={item.id}>
                  <Link
                    to={`/article/${item.id}`}
                    className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-4 sm:w-64 shrink-0">
                      <span
                        className={`w-10 h-10 shrink-0 rounded-md flex items-center justify-center font-heading font-black text-lg ${
                          index < 3
                            ? "bg-primary-500 text-background-50"
                            : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {index + 1}
                      </span>
                      <div className="w-20 h-16 rounded-md overflow-hidden bg-background-200 shrink-0">
                        <img
                          src={item.cover}
                          alt={item.title}
                          title={`${item.title} - 51吃瓜`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <span className="hidden sm:block text-xs text-foreground-500">
                        {getCategoryName(item.category)}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <h2 className="font-heading font-bold text-base md:text-lg text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                        {item.title}
                      </h2>
                      <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        {item.excerpt}
                      </p>
                      <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-foreground-400">
                        <span className="sm:hidden px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800">
                          {getCategoryName(item.category)}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {formatViews(item.views)}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-fire-line"></i>
                          热度 {item.heat}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {item.readMinutes} 分钟
                        </span>
                      </div>
                      <div className="mt-3 h-1.5 w-full rounded-full bg-background-200 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-primary-500"
                          style={{ width: `${Math.round((item.heat / maxHeat) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ol>

            {ranked.length === 0 && (
              <p className="text-center text-foreground-500 py-16">
                这个分类暂时还没有上榜的瓜，换个分类看看吧。
              </p>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
      <ScrollToTop />
    </div>
  );
}